// Webes push-értesítések (4.3).
//
// A szerződés a Flutter oldallal KÖZÖS, és a cloud function írja elő:
//   users/{uid}.fcmTokens  - a felhasználó eszköztokenjei (tömb)
//   a push `data` mezőjében `taskId` érkezik
// (lásd cloudfunctions/sendPushToUsers.js és
//  lib/commons/services/firebase_msg_service.dart)
//
// A Flutter oldal SZÁNDÉKOSAN kihagyja a webet (`if (!kIsWeb)`), tehát a
// böngészős tokent ennek a modulnak kell felvennie — enélkül a webes
// felhasználó soha nem kap push-t, hiába működik minden a szerveren.
//
// FONTOS: a push NEM mindenhol elérhető. Kell hozzá HTTPS (vagy localhost),
// service worker, Notification API, és a Firebase-projekt VAPID kulcsa. Ha
// bármelyik hiányzik, a modul ezt MEGMONDJA — nem tesz úgy, mintha menne.

import { arrayRemove, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import type { Messaging, MessagePayload } from 'firebase/messaging';

import { app, db } from './firebase';

/// A push-értesítés engedélyezésének lehetséges kimenetei.
///
/// Külön eset a „nincs VAPID kulcs": ilyenkor nem a felhasználó tagadta meg,
/// hanem a telepítés hiányos — más üzenet jár neki.
export type PushOutcome =
  | 'ok'
  | 'denied'
  | 'unsupported'
  | 'missing-key'
  | 'failed';

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

let messagingInstance: Messaging | undefined;
let registration: ServiceWorkerRegistration | undefined;

/// A böngésző tud-e egyáltalán push-t fogadni.
///
/// A `isSupported()` a Firebase saját ellenőrzése (IndexedDB, service worker,
/// Push API); emellett a Notification API meglétét is nézzük, mert iOS-en
/// régebbi verziókban ez hiányzik.
export async function isPushAvailable(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

/// Van-e beállítva VAPID kulcs. Enélkül a `getToken` mindig elhasalna.
export function hasVapidKey(): boolean {
  return typeof vapidKey === 'string' && vapidKey.length > 0;
}

/// A jelenlegi engedély-állapot, felületre kiírható alakban.
export function permissionState(): NotificationPermission | 'unavailable' {
  if (typeof Notification === 'undefined') return 'unavailable';
  return Notification.permission;
}

/// A service worker regisztrálása.
///
/// Fejlesztés közben a Vite a TS-forrást szolgálja ki, élesben a build által
/// a gyökérbe tett `firebase-messaging-sw.js` áll rendelkezésre. Mindkettő
/// ESM, ezért modulként regisztráljuk.
async function registerWorker(): Promise<ServiceWorkerRegistration> {
  if (registration) return registration;
  const url = import.meta.env.DEV
    ? '/src/firebase-messaging-sw.ts'
    : '/firebase-messaging-sw.js';
  registration = await navigator.serviceWorker.register(url, { type: 'module' });
  return registration;
}

function messaging(): Messaging {
  messagingInstance ??= getMessaging(app);
  return messagingInstance;
}

/// Push bekapcsolása: engedélykérés, token lekérése, majd a token felvétele a
/// felhasználó dokumentumára.
///
/// A token EGY BÖNGÉSZŐRE szól. Ugyanaz a felhasználó több gépen és több
/// böngészőben is dolgozhat, ezért a mező tömb, és `arrayUnion`-nal bővül —
/// felülírással a korábbi eszközök némán kiesnének.
export async function enablePush(userId: string): Promise<PushOutcome> {
  if (!(await isPushAvailable())) return 'unsupported';
  if (!hasVapidKey()) return 'missing-key';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  try {
    const serviceWorkerRegistration = await registerWorker();
    const token = await getToken(messaging(), {
      vapidKey,
      serviceWorkerRegistration,
    });
    if (!token) return 'failed';

    await updateDoc(doc(db, 'users', userId), { fcmTokens: arrayUnion(token) });
    return 'ok';
  } catch (error) {
    console.warn('[BlazeData] A push bekapcsolása nem sikerült.', error);
    return 'failed';
  }
}

/// Push kikapcsolása EZEN a böngészőn.
///
/// A tokent a szerver oldaláról is el kell venni, különben a cloud function
/// tovább küldene rá — a felhasználó pedig azt látná, hogy a kapcsoló hazudik.
export async function disablePush(userId: string): Promise<boolean> {
  if (!(await isPushAvailable())) return false;
  try {
    const serviceWorkerRegistration = await registerWorker();
    const token = await getToken(messaging(), { vapidKey, serviceWorkerRegistration });
    if (token) {
      await updateDoc(doc(db, 'users', userId), { fcmTokens: arrayRemove(token) });
      await deleteToken(messaging());
    }
    return true;
  } catch (error) {
    console.warn('[BlazeData] A push kikapcsolása nem sikerült.', error);
    return false;
  }
}

/// Előtérben érkező üzenet.
///
/// Ilyenkor a rendszer NEM mutat értesítést — a lap kapja meg. A hívó dolga
/// eldönteni, mit kezd vele (a mi esetünkben: rövid visszajelzés és az
/// értesítés-lista frissítése).
export function onPushInForeground(
  handler: (message: { title: string; body: string; taskId?: string }) => void,
): void {
  void isPushAvailable()
    .then((available) => {
      if (!available) return;
      // `isSupported()` a BÖNGÉSZŐ képességeit nézi (IndexedDB, service
      // worker, Push API) — a `getMessaging()` ettől még elhasalhat, ha a
      // Firebase-példányon nincs messaging-összetevő. Emulátorra kötve
      // pontosan ez történik: „Service messaging is not available".
      //
      // A lánc végén ezért ott a `catch`: a feliratkozás elmaradása nem ok
      // arra, hogy az app egy KEZELETLEN elutasítást dobjon a konzolba. Pont
      // ez történt, amíg hiányzott — és épp az a fajta hiba, amit egyetlen
      // ellenőrző szkript sem lát, csak a futó app konzolja.
      onMessage(messaging(), (payload: MessagePayload) => {
        handler({
          title: payload.notification?.title ?? payload.data?.['title'] ?? 'BlazeData',
          body: payload.notification?.body ?? payload.data?.['body'] ?? '',
          taskId: payload.data?.['taskId'],
        });
      });
    })
    .catch((error: unknown) => {
      console.warn('[BlazeData] Az előtérben érkező push figyelése nem indult el.', error);
    });
}
